import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./learningStyle.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { uploadLsImage } from "../../functions/uploadLsImage";
import { useDocumentTitle } from "../../functions/useDocumentTitle";

const CATEGORY_LABEL_KEYS = ["option1", "option2", "option3", "option4", "option5", "option6"];
const CATEGORY_ICON_KEYS = ["cat_icon1", "cat_icon2", "cat_icon3", "cat_icon4", "cat_icon5", "cat_icon6"];
const s3Url = (key) => (key ? `${process.env.REACT_APP_S3_PATH || ""}${key}` : "");

const MAX_LABEL_LEN = 30;
const MAX_HEADING_LEN = 30;
const DEFAULT_HEADING = "Your Learning Style";
const VALID_ICON_EXT = ["png", "gif"];
const ICON_SIZE = 400;

const MAX_MSG_HEADING_LEN = 40;
const MAX_MSG_TEXT_LEN = 100;
const DEFAULT_PRIMARY_HEADING = "As Primary:";
const DEFAULT_SECONDARY_HEADING = "As Secondary:";
const emptyMessage = () => ({
  primaryHeading: DEFAULT_PRIMARY_HEADING,
  primaryText: "",
  secondaryHeading: DEFAULT_SECONDARY_HEADING,
  secondaryText: "",
});

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

async function validateIconFile(file) {
  const ext = file.name.split(".").pop().toLowerCase();
  if (!VALID_ICON_EXT.includes(ext)) {
    return `Only ${VALID_ICON_EXT.join(", ")} formats allowed`;
  }
  try {
    const { width, height } = await getImageDimensions(file);
    if (width !== ICON_SIZE || height !== ICON_SIZE) {
      return `Icon image size must be ${ICON_SIZE}x${ICON_SIZE}`;
    }
  } catch {
    return "Failed to validate image dimensions";
  }
  return null;
}

function CategorySettings() {
  useDocumentTitle("Labels Settings - Learning Style Admin");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data: themeData, status } = useSelector((state) => state.theme);

  const [tab, setTab] = useState("labels");
  const [categoryLabels, setCategoryLabels] = useState(["", "", "", "", "", ""]);
  const [headingText, setHeadingText] = useState(DEFAULT_HEADING);
  const [badgeEnabled, setBadgeEnabled] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploadingIconIdx, setUploadingIconIdx] = useState(null);
  const [messages, setMessages] = useState(() => CATEGORY_LABEL_KEYS.map(() => emptyMessage()));
  const [savingMessages, setSavingMessages] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!themeData) return;
    setCategoryLabels(CATEGORY_LABEL_KEYS.map((k) => themeData[k] || ""));
    setHeadingText(themeData.thankyou_heading || DEFAULT_HEADING);
    setBadgeEnabled(themeData.badge_enabled == null ? true : !!themeData.badge_enabled);
    setMessages(
      CATEGORY_LABEL_KEYS.map((_, i) => {
        const n = i + 1;
        return {
          primaryHeading: themeData[`primary_heading_${n}`] || DEFAULT_PRIMARY_HEADING,
          primaryText: themeData[`primary_text_${n}`] || "",
          secondaryHeading: themeData[`secondary_heading_${n}`] || DEFAULT_SECONDARY_HEADING,
          secondaryText: themeData[`secondary_text_${n}`] || "",
        };
      })
    );
  }, [themeData]);

  const handleMessageFieldChange = (idx, field, value) => {
    setMessages((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleSaveMessages = async () => {
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      const label = categoryLabels[i]?.trim() || `Option ${i + 1}`;
      if (!m.primaryHeading.trim() || !m.primaryText.trim() || !m.secondaryHeading.trim() || !m.secondaryText.trim()) {
        Swal.fire("Error", `Please fill in all four Primary/Secondary fields for "${label}" — they're required before players can play.`, "error");
        return;
      }
      if (m.primaryHeading.trim().length > MAX_MSG_HEADING_LEN || m.secondaryHeading.trim().length > MAX_MSG_HEADING_LEN) {
        Swal.fire("Error", `Each heading must be ${MAX_MSG_HEADING_LEN} characters or less.`, "error");
        return;
      }
      if (m.primaryText.trim().length > MAX_MSG_TEXT_LEN || m.secondaryText.trim().length > MAX_MSG_TEXT_LEN) {
        Swal.fire("Error", `Each message must be ${MAX_MSG_TEXT_LEN} characters or less.`, "error");
        return;
      }
    }
    setSavingMessages(true);
    try {
      const payload = {};
      messages.forEach((m, i) => {
        const n = i + 1;
        payload[`primary_heading_${n}`] = m.primaryHeading.trim() || DEFAULT_PRIMARY_HEADING;
        payload[`primary_text_${n}`] = m.primaryText.trim();
        payload[`secondary_heading_${n}`] = m.secondaryHeading.trim() || DEFAULT_SECONDARY_HEADING;
        payload[`secondary_text_${n}`] = m.secondaryText.trim();
      });
      await updateThemeData({
        payload: { data: payload, organizationId: organizationId || "admin", sessionId: sessionId || "admin", currentTheme },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Messages Updated", "", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save messages", "error");
    } finally {
      setSavingMessages(false);
    }
  };

  const handleSaveSettings = async () => {
    if (categoryLabels.some((v) => !v.trim())) {
      Swal.fire("Please fill in all the fields.", "", "warning");
      return;
    }
    if (categoryLabels.some((v) => v.trim().length > MAX_LABEL_LEN)) {
      Swal.fire("Error", `Each label must be ${MAX_LABEL_LEN} characters or less.`, "error");
      return;
    }
    if (headingText.trim().length > MAX_HEADING_LEN) {
      Swal.fire("Error", `Heading must be ${MAX_HEADING_LEN} characters or less.`, "error");
      return;
    }
    setSavingSettings(true);
    try {
      const payload = { thankyou_heading: headingText.trim() || DEFAULT_HEADING };
      CATEGORY_LABEL_KEYS.forEach((key, i) => {
        payload[key] = categoryLabels[i].trim();
      });
      await updateThemeData({
        payload: { data: payload, organizationId: organizationId || "admin", sessionId: sessionId || "admin", currentTheme },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Settings Updated", "", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save settings", "error");
    } finally {
      setSavingSettings(false);
    }
  };

  /** Saves immediately on toggle — same pattern as the icon uploads below. */
  const handleToggleBadge = async (checked) => {
    setBadgeEnabled(checked);
    try {
      await updateThemeData({
        payload: {
          data: { badge_enabled: checked },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
    } catch (e) {
      setBadgeEnabled(!checked);
      Swal.fire("Error", e.message || "Failed to update", "error");
    }
  };

  /** Uploads straight from each label's own row and saves immediately — no separate pick-then-upload step. */
  const handleCatIconUpload = async (idx, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validationError = await validateIconFile(file);
    if (validationError) {
      Swal.fire("Invalid Image", validationError, "error");
      e.target.value = "";
      return;
    }

    setUploadingIconIdx(idx);
    try {
      const key = await uploadLsImage({ file, token: adminToken });
      await updateThemeData({
        payload: {
          data: { [CATEGORY_ICON_KEYS[idx]]: key },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
    } catch (err) {
      Swal.fire("Error", err.message || "Upload failed", "error");
    } finally {
      setUploadingIconIdx(null);
      e.target.value = "";
    }
  };

  if (status === "loading" && !themeData) {
    return (
      <div className="ls-admin-page">
        <div className="ls-admin-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="ls-admin-page ls-admin-page-full">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/questions")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="ls-admin-center-wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 className="ls-admin-title" style={{ margin: 0 }}>Labels Settings</h2>
        <div className="ls-admin-toggle-row" style={{ margin: 0 }}>
          <label className="ls-admin-label" style={{ margin: 0 }}>Show badge &amp; image on Thank You page</label>
          <input
            type="checkbox"
            checked={badgeEnabled}
            onChange={(e) => handleToggleBadge(e.target.checked)}
          />
        </div>
      </div>

      <div className="ls-admin-center-wrap">
        <div className="ls-admin-tabs">
          <button
            type="button"
            className={`ls-admin-tab ${tab === "labels" ? "active" : ""}`}
            onClick={() => setTab("labels")}
          >
            <i className="fa-solid fa-tag" /> Labels
          </button>
          <button
            type="button"
            className={`ls-admin-tab ${tab === "images" ? "active" : ""}`}
            onClick={() => setTab("images")}
          >
            <i className="fa-solid fa-images" /> Label Images
          </button>
          <button
            type="button"
            className={`ls-admin-tab ${tab === "messages" ? "active" : ""}`}
            onClick={() => setTab("messages")}
          >
            <i className="fa-solid fa-comment-dots" /> Messages
          </button>
        </div>

        {tab === "labels" && (
          <div className="ls-admin-tab-panel">
            <div className="ls-admin-field" style={{ maxWidth: 360, margin: "0 auto 20px" }}>
              <span className="ls-admin-field-caption">Thank You Page Heading</span>
              <input
                type="text"
                className="ls-admin-input"
                placeholder={DEFAULT_HEADING}
                maxLength={MAX_HEADING_LEN}
                value={headingText}
                onChange={(e) => setHeadingText(e.target.value)}
              />
              <p className="code" style={{ textAlign: "center" }}>* Max length {MAX_HEADING_LEN} characters</p>
            </div>

            <p className="ls-admin-muted ls-admin-tab-panel-intro">Options for the answers are based upon:</p>
            <div className="ls-admin-labels-grid">
              {categoryLabels.map((val, i) => (
                <div className="ls-admin-field" key={i}>
                  <span className="ls-admin-field-caption">Option {i + 1}</span>
                  <input
                    type="text"
                    className="ls-admin-input"
                    placeholder={`Option ${i + 1}`}
                    maxLength={MAX_LABEL_LEN}
                    value={val}
                    onChange={(e) => {
                      const arr = [...categoryLabels];
                      arr[i] = e.target.value;
                      setCategoryLabels(arr);
                    }}
                  />
                </div>
              ))}
            </div>
            <p className="code ls-admin-tab-panel-intro" style={{ textAlign: "center" }}>
              * User will get these options on the related set of questions
              <br />* Max length {MAX_LABEL_LEN} characters
            </p>

            <div className="ls-admin-action-holder">
              <button type="button" className="ls-admin-save-btn" onClick={handleSaveSettings} disabled={savingSettings}>
                {savingSettings ? "Saving..." : "Save Labels"}
              </button>
            </div>
          </div>
        )}

        {tab === "images" && (
          <div className="ls-admin-tab-panel">
            <p className="ls-admin-muted ls-admin-tab-panel-intro">
              Upload an icon for each label — it's saved as soon as you pick a file.
            </p>
            <p className="code ls-admin-tab-panel-intro" style={{ textAlign: "center" }}>
              * Allowed png, gif Format
              <br />* Icon image size must be {ICON_SIZE}x{ICON_SIZE}
            </p>
            <div className="ls-admin-cat-icon-grid">
              {CATEGORY_LABEL_KEYS.map((labelKey, i) => {
                const label = categoryLabels[i]?.trim() || `Option ${i + 1}`;
                const iconValue = themeData?.[CATEGORY_ICON_KEYS[i]];
                return (
                  <div key={labelKey} className="ls-admin-cat-icon-row">
                    <div className="ls-admin-cat-icon-preview">
                      {iconValue ? (
                        <img src={s3Url(iconValue)} alt={label} />
                      ) : (
                        <span className="ls-admin-cat-icon-placeholder">No image</span>
                      )}
                    </div>
                    <div className="ls-admin-cat-icon-info">
                      <div className="ls-admin-cat-icon-label">{label}</div>
                      <input
                        type="file"
                        accept="image/png,image/gif"
                        onChange={(e) => handleCatIconUpload(i, e)}
                        disabled={uploadingIconIdx === i}
                      />
                      {uploadingIconIdx === i && <span className="ls-admin-muted"> Uploading...</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div className="ls-admin-tab-panel">
            <p className="ls-admin-muted ls-admin-tab-panel-intro">
              The heading and message shown on the Thank You page when this label comes out as a player's primary or secondary learning style.
            </p>
            <p className="code ls-admin-tab-panel-intro" style={{ textAlign: "center" }}>
              * All four fields are required for every label — the quiz won't let players start until they're filled in
              <br />* Heading max {MAX_MSG_HEADING_LEN} characters, message max {MAX_MSG_TEXT_LEN} characters
            </p>

            {CATEGORY_LABEL_KEYS.map((labelKey, i) => {
              const label = categoryLabels[i]?.trim() || `Option ${i + 1}`;
              const m = messages[i];
              return (
                <div key={labelKey} className="ls-admin-message-card">
                  <div className="ls-admin-message-card-title">{label}</div>
                  <div className="ls-admin-message-row">
                    <div className="ls-admin-message-col">
                      <span className="ls-admin-message-col-label">As Primary</span>
                      <input
                        type="text"
                        className="ls-admin-input"
                        placeholder="Heading"
                        maxLength={MAX_MSG_HEADING_LEN}
                        value={m.primaryHeading}
                        onChange={(e) => handleMessageFieldChange(i, "primaryHeading", e.target.value)}
                      />
                      <textarea
                        className="ls-admin-textarea"
                        placeholder={`Your primary learning style is ${label}. ...`}
                        maxLength={MAX_MSG_TEXT_LEN}
                        value={m.primaryText}
                        onChange={(e) => handleMessageFieldChange(i, "primaryText", e.target.value)}
                      />
                    </div>
                    <div className="ls-admin-message-col">
                      <span className="ls-admin-message-col-label">As Secondary</span>
                      <input
                        type="text"
                        className="ls-admin-input"
                        placeholder="Heading"
                        maxLength={MAX_MSG_HEADING_LEN}
                        value={m.secondaryHeading}
                        onChange={(e) => handleMessageFieldChange(i, "secondaryHeading", e.target.value)}
                      />
                      <textarea
                        className="ls-admin-textarea"
                        placeholder={`Your secondary learning style is ${label}. ...`}
                        maxLength={MAX_MSG_TEXT_LEN}
                        value={m.secondaryText}
                        onChange={(e) => handleMessageFieldChange(i, "secondaryText", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="ls-admin-action-holder">
              <button type="button" className="ls-admin-save-btn" onClick={handleSaveMessages} disabled={savingMessages}>
                {savingMessages ? "Saving..." : "Save Messages"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CategorySettings;
